<template>
	<div>
		<v-text-field
			label="Scene Name"
			:value="form.name"
			:readonly="!editMode"
			:rules="[rules.required, rules.nameUnique, rules.nameValid]"
			@input="updateField('name', $event)"
		/>

		<h4>Trigger</h4>
		<v-select
			label="Device"
			return-object
			item-value="path"
			:item-text="deviceName"
			:items="triggerDevices"
			:value="form.trigger.device"
			:readonly="!editMode"
			:append-icon="editMode ? 'fa-caret-down' : ''"
			:rules="[rules.required]"
			@input="updateField('trigger.device', $event)"
			@change="updateField('trigger.path', null)"
		/>
		<v-select
			label="Event"
			:items="triggerItems"
			:value="form.trigger.path"
			:readonly="!editMode"
			:append-icon="editMode ? 'fa-caret-down' : ''"
			:rules="[rules.required]"
			@input="updateField('trigger.path', $event)"
			@change="resetArgs"
		/>
		<div v-if="isTriggerVariable" class="d-flex">
			<v-select
				v-if="!isTriggerBoolean"
				:items="operators"
				:value="form.trigger.operator"
				:readonly="!editMode"
				:rules="[rules.required]"
				@input="updateField('trigger.operator', $event)"
			/>
			<k4-model-value-field
				:value="form.trigger.value"
				:node="form.trigger.device"
				:subpath="triggerVariableSubpath"
				:readonly="!editMode"
				required
				@input="updateField('trigger.value', $event)"
			/>
		</div>

		<h4>Set</h4>
		<v-select
			label="Device"
			return-object
			item-value="path"
			:item-text="deviceName"
			:items="targetDevices"
			:value="form.target.device"
			:readonly="!editMode"
			:append-icon="editMode ? 'fa-caret-down' : ''"
			:rules="[rules.required]"
			@input="updateField('target.device', $event)"
			@change="updateField('target.path', null)"
		/>
		<v-select
			label="Command"
			:items="targetCommands"
			:value="form.target.path"
			:readonly="!editMode"
			:append-icon="editMode ? 'fa-caret-down' : ''"
			:rules="[rules.required]"
			@input="updateField('target.path', $event)"
			@change="updateField('target.args', {})"
		/>
		<k4-model-arg-form
			:args="form.target.args"
			:node="form.target.device"
			:subpath="targetSubpath"
			@input="updateField('target.args', $event)"
		/>

		<h4>When</h4>
		<shared-time-field
			label="Between"
			:clearable="editMode"
			:value="form.time.from"
			:readonly="!editMode"
			:rules="[rules.requiredIf(form.time.to)]"
			@input="updateField('time.from', $event)"
		/>
		<shared-time-field
			label="and"
			:clearable="editMode"
			:value="form.time.to"
			:readonly="!editMode"
			:rules="[rules.requiredIf(form.time.from)]"
			@input="updateField('time.to', $event)"
		/>
	</div>
</template>

<script>
import _ from "lodash";
import { mapGetters } from "vuex";
import { DEFAULT_OPERATORS_MAP } from "@/services/k4/node/conditionNode";

const initData = () => ({
	operators: _.map(DEFAULT_OPERATORS_MAP, (val, key) => ({
		text: val,
		value: key,
	})),

	updatedFields: [],

	form: {
		name: "",
		trigger: {
			device: null,
			path: null,
			operator: null,
			value: null,
		},
		target: {
			device: null,
			path: null,
			args: {},
		},
		time: {
			from: "",
			to: "",
		},
	},
});

export default {
	props: {
		scene: Object,
	},

	data: initData,
	computed: {
		...mapGetters("scenes", ["scenes", "sceneNodes"]),
		...mapGetters("controller/nodes", ["nodeByPath"]),

		triggerDevices() {
			return _.filter(
				this.$store.getters["controller/nodes/nodes"],
				(node) => _.has(node, "variables") || _.has(node, "events")
			);
		},

		triggerVariables() {
			return this.getItemsFromDevice(this.form.trigger.device, "variables");
		},

		triggerItems() {
			return [
				...this.triggerVariables,
				...this.getItemsFromDevice(this.form.trigger.device, "events"),
			];
		},

		isTriggerVariable() {
			return ~this.triggerVariables.findIndex(
				({ value }) => value === this.form.trigger.path
			);
		},

		triggerVariableSubpath() {
			if (!this.form.trigger.device || !this.form.trigger.path) return "";

			const devicePath = this.form.trigger.device.path;
			const varPath = this.form.trigger.path;
			return varPath.substr(devicePath.length + 1);
		},

		isTriggerBoolean() {
			const varNode = this.$store.getters["controller/nodes/liveNodeByPath"](
				this.form.trigger.path
			);
			const meta = varNode ? varNode.meta() : null;
			const type = _.get(meta, "type");
			return type === "boolean";
		},

		targetDevices() {
			return this.$store.getters["controller/nodes/devicesByType"]("commands");
		},

		targetSubpath() {
			if (!this.form.target.path) return null;
			return this.form.target.path.split("/").slice(-2).join("/");
		},

		targetCommands() {
			if (!this.form.target.device) return [];

			const devicePath = this.form.target.device.path;
			let commands = _.get(this.form.target.device, "commands", []);
			commands = _.keys(commands);
			commands = _.map(commands, (commandName) => ({
				text: _.startCase(commandName),
				value: `${devicePath}/commands/${commandName}`,
			}));

			return commands;
		},

		rules() {
			return {
				required: (v) => !!v || "Field cannot be empty!",
				requiredIf: (v) => (condition) =>
					!!condition || !v || "Field cannot be empty!",
				nameUnique: () =>
					!this.editMode ||
					_.get(this.scene, "name") === this.form.name ||
					!this.scenes.map((s) => s.name).includes(this.form.name) ||
					"A scene with this name already exists!",
				nameValid: (v) => !v.match(/\//) || "Invalid character!",
			};
		},

		editMode() {
			return this.$store.getters["scenes/editMode"] || !this.scene;
		},
	},

	watch: {
		scene(val) {
			if (val && !this.editMode) this.parseScene(val);
		},

		form: {
			deep: true,
			handler(form) {
				this.$emit("update:form", form);
			},
		},

		updatedFields: {
			deep: true,
			handler(fields) {
				this.$emit("update:fields", fields);
			},
		},
	},

	created() {
		if (this.scene) this.parseScene(this.scene);
	},

	methods: {
		deviceName({ path, name }) {
			const area = path.split("/").reverse()[1];
			return `${area} ${name}`;
		},

		getItemsFromDevice(device, type) {
			if (!device || !type) return [];

			const devicePath = device.path;
			let items = _.get(device, type, []);
			items = _.keys(items);
			items = _.map(items, (conditionName) => ({
				text: _.startCase(conditionName),
				value: `${devicePath}/${type}/${conditionName}`,
			}));

			return items;
		},

		resetArgs() {
			this.form.trigger.operator = "==";
			this.form.trigger.value = null;
		},

		updateField(field, value) {
			_.set(this.form, field, value);
			const fieldBlock = field.split(".")[0];
			if (!this.updatedFields.includes(fieldBlock))
				this.updatedFields.push(fieldBlock);
		},

		parseScene(scene) {
			this.$emit("reset");
			const { commands, timeConditions, ifConditions, dateTimeOrConditions } =
				this.sceneNodes;

			const condition = ifConditions[0];
			this.form.trigger.device = this.nodeByPath(
				_.get(condition, "target.parentDevice.path")
			);
			this.form.trigger.path = _.get(condition, "targetPath");
			this.form.trigger.operator = _.get(condition, "operator");
			this.form.trigger.value = _.get(condition, "value");

			const step = commands[0];
			this.form.target.device = this.nodeByPath(
				_.get(step, "target.parentDevice.path")
			);
			this.form.target.path = _.get(step, "targetPath");
			this.form.target.args = _.get(step, "args", {});

			const times = _.filter([
				...timeConditions,
				..._.get(dateTimeOrConditions, "0.children", []),
			], "targetIsTime");

			const from = times.find((node) => node.operator === ">=");
			const to = times.find((node) => node.operator === "<=");
			this.form.time.from = _.get(from, "value");
			this.form.time.to = _.get(to, "value");

			this.form.name = _.get(scene, "name", "");

			this.checkExtraNodes();
		},

		checkExtraNodes() {
			let sceneLength = _.filter([
				this.form.time.from,
				this.form.time.to,
				this.form.trigger.path,
				this.form.target.path,
			]).length;
			const hasExtra =
				_.reduce(
					this.sceneNodes,
					(length, nodes) =>
						length +
						nodes.reduce((l, node) => l + node.children.length || 1, 0),
					0
				) > sceneLength;

			this.$emit("update:extra", hasExtra);
		},

		reset() {
			if (this.scene) this.parseScene(this.scene);
			else Object.assign(this, initData());
		},
	},
};
</script>

<style lang="scss" scoped>
::v-deep.v-select .v-select__selections input {
	display: none;
}
</style>
